import { graphql } from "gatsby";
import React, { useState, useCallback } from "react";
import Markdown from "react-markdown";
import { siteMetadata } from "../../site-metadata";
import { categoryParams } from "../../node-category";
import Breadcrumbs from "../components/breadcrumbs/breadcrumbs";
import CategoryTree from "../components/category-tree/category-tree";
import Layout from "../components/layout";
import ProductList from "../components/product-list/product-list";
import SearchForm from "../components/search-form/search-form";
import Separator from "../components/separator/separator";
import IconsLineCat from "../components/icons-line-cat/icons-line-cat";
import BandeauJute from "../assets/icons/bandeau-site-pomo-jute.svg"
import Filter, { filterProducts, ActiveFilters } from "../components/filter/filter"

export const query = graphql`
  query CategoryQuery($slug: String!) {
    strapiCategory(slug: { eq: $slug }) {
      id
      description
      metatitle
      metadescription
      page_header
      page_content
      visuel {
        localFile {
          publicURL
          childImageSharp {
            gatsbyImageData(width: 400, height: 400)
          }
        }
      }
      slug
      strapiId
      strapiParent {
          id
        }
      name

    }
    allStrapiProduct(
      filter: {
        categories: { elemMatch: { slug: { eq: $slug } } }
        status: { eq: "published" }
      }
      sort: {
        position: ASC
      }
    ) {
      edges {
        node {
          slug
          status
          strapiId
          position
          title
          description
          categories {
            name
            id
            slug
          }
          image {
            localFile {
              childImageSharp {
                gatsbyImageData(
                  width: 300
                  height: 365
                  transformOptions: { cropFocus: SOUTH }
                )
              }
            }
          }
          ...ProductCardFields
          variations {
            attributs_couleur {
              Nom
              valeur
              id
            }
            Image {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 300
                    height: 365
                    transformOptions: { cropFocus: SOUTH }
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`;

const Category = ({ data, location }) => {
  const [, setFilterVersion] = useState(0);
  const onFilterChange = useCallback(() => setFilterVersion((v) => v + 1), []);

  const category = data.strapiCategory;
  const allProducts = data.allStrapiProduct.edges.map((e) => e.node);
  // attribut « couleur » à plat pour le filtre Color
  for (const product of allProducts) {
    product.attributs_couleur = (product.variations || []).flatMap((el) => el.attributs_couleur).filter(Boolean);
  }
  const currentLocation = typeof window !== "undefined"
    ? { ...location, search: window.location.search }
    : location;
  const products = filterProducts(allProducts, currentLocation);

  const canonicalReplacements = {};
  const canonicalReplacement = canonicalReplacements[category.slug] || category.slug;

  const seo = {
    metaTitle: category.metatitle || category.name,
    metaDescription: category.metadescription || category.description,
    shareImage: category.visuel,
    article: false,
    canonical: siteMetadata.siteUrl + categoryParams.urlPrefix + canonicalReplacement,
    scripts: [
      {
        type: "application/ld+json",
        content: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": category.name,
          ...(category.description && { "description": category.description }),
          "url": `https://tallerdeltotebag.es/${category.slug}`,
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "item": {
                  "@id": "https://tallerdeltotebag.es",
                  "name": "Inicio"
                }
              },
              {
                "@type": "ListItem",
                "position": 2,
                "item": {
                  "@id": `https://tallerdeltotebag.es/${category.slug}`,
                  "name": category.name
                }
              }
            ]
          },
          "mainEntity": [
            {
              "@type": "ItemList",
              "itemListElement": allProducts.map((product, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "Product",
                  "name": product.title,
                  "url": `https://tallerdeltotebag.es/${product.slug}`,
                  "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": 4.9,
                    "reviewCount": 620
                  }
                }
              }))
            }
          ]
        })
      },
    ]
  };

  return (
    <Layout seo={seo} showHero={false}>
      {category.slug === "###BANNIERE PROMO TEMP## - bolsos-yute" && (
        <div >
          <BandeauJute className="w-full" />
        </div>
      )}
      <div className="bg-gray-100">
        <div className="container py-4">
          <h1 className="text-center">{category.name}</h1>
          <div className="text-center my-3">
            <Markdown>{category.page_header}</Markdown>
            <IconsLineCat />
          </div>
        </div>
      </div>
      <div className="py-4">
        <div className="my-4">
          <Breadcrumbs category={category} />
        </div>
        <div className="flex flex-wrap ">
          <div className="w-full order-2 md:order-1 md:w-3/12 md:pr-4">
            <span className="h3 block text-base font-semibold uppercase pb-2 border-b-3 border-gray-200">
              Buscar un producto
            </span>
            <SearchForm />
            <div className="mt-8">
              <Filter products={allProducts} location={location} onFilterChange={onFilterChange} />
            </div>
            <span className="h3 block mt-8 text-base font-semibold uppercase pb-2 border-b-3 border-gray-200">
              Categorías
            </span>
            <CategoryTree currentCat={category} shouldFindRoot={true} />

          </div>
          <div className="w-full order-1 md:order-2 md:w-9/12">
            <span className="font-semibold text-gray-700">
              Descubra todos los tote bags personalizados, propuestos y fabricados
              por el taller del tote bag.
            </span>
            <ActiveFilters onFilterChange={onFilterChange} />
            <div>{products.length === 1 ? "Hay 1 producto" : `Hay ${products.length} productos`}</div>
            <ProductList products={products} />
          </div>
        </div>
        <hr className="my-4" />

        <div className="cat-content">
          <Markdown>{category.page_content}</Markdown>
        </div>
        <div className="cat-side">
        </div>
      </div>
    </Layout>
  );
};

export default Category;
